import type {
  LookbookItemDataFragment,
  LookbookSectionDataFragment,
} from "./graphql/types";
import type { ProductDataFragment } from "./shopify-graphql/types";

export async function parseJsonOrIgnore(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return undefined;
  }
}

export function hasGraphQLErrors(response: unknown | undefined): response is {
  errors: { message: string; extensions?: { code?: string } }[];
} {
  return (
    isPlainObject(response) &&
    hasProperty(response, "errors") &&
    Array.isArray(response.errors)
  );
}

export function hasProperty<O extends object, K extends string>(
  obj: O,
  key: K,
): obj is O & { [Key in K]: unknown } {
  return Object.prototype.hasOwnProperty.call(obj, key);
}

export function isPlainObject(obj: unknown): obj is Record<string, unknown> {
  return typeof obj === "object" && obj !== null;
}

export type Maybe<T> = T | null | undefined;

export type ElementOf<T> = T extends readonly (infer U)[]
  ? U
  : T extends (infer U)[]
    ? U
    : never;

export function formatShopifyProductId(baseProductId: string) {
  return `gid://shopify/Product/${baseProductId}`;
}

export function getShopifyProductFromItem(
  shopifyProducts: ProductDataFragment[],
  item: LookbookItemDataFragment | LookbookSectionDataFragment,
): ProductDataFragment | undefined {
  const [currentShopifyProduct] = shopifyProducts.filter((p) => {
    return (
      p.id &&
      "resource" in item &&
      item.resource &&
      "external_id" in item.resource &&
      p.id === formatShopifyProductId(item.resource.external_id)
    );
  });

  return currentShopifyProduct;
}

export function getProductIdsFromSections(
  sections: LookbookSectionDataFragment[],
  checkoutIntegrationId: string | null,
) {
  if (!checkoutIntegrationId) {
    return [];
  }

  return sections.flatMap((section) =>
    section.items.flatMap((item) =>
      item.resource?.__typename === "Product" &&
      item.resource.integration_id === checkoutIntegrationId
        ? [formatShopifyProductId(item.resource.external_id)]
        : [],
    ),
  );
}

export function createShopifyStorefrontApiUrl(
  storeDomain: string,
  apiVersion: string,
) {
  return `https://${storeDomain}/api/${apiVersion}/graphql.json`;
}
